import { z } from 'zod';
import { staffDetailsSchema } from '@/features/institute/staff/schemas/staff.schema';

/**
 * A member of staff keeping their own record up to date, from My Profile.
 *
 * Mirrors `updateOwnProfileSchema` in the API's `staff.validation.ts`, and picks
 * its fields from the office form's own `staffDetailsSchema` — so an Aadhaar or
 * an IFSC this form accepts is one the office's would, and the rules are not
 * written twice (CLAUDE.md §3). The server checks everything again, and it alone
 * checks the password (§6).
 */

/** Indian mobile numbers are ten digits and never begin below 6. */
const MOBILE_PATTERN = /^[6-9]\d{9}$/;

export const ownDetailsSchema = staffDetailsSchema.pick({
  dateOfBirth: true,
  gender: true,
  bloodGroup: true,
  maritalStatus: true,
  nationality: true,
  aadhaarNumber: true,
  panNumber: true,
  uanNumber: true,
  passportNumber: true,
  alternateMobile: true,
  emergencyContactName: true,
  emergencyContactMobile: true,
  presentAddress: true,
  permanentAddress: true,
  bankName: true,
  bankAccountNumber: true,
  bankIfsc: true,
  bankAccountHolder: true,
  qualification: true,
  languagesKnown: true,
  experienceYears: true,
  skills: true,
});

export const ownProfileSchema = z.object({
  mobile: z.string().trim().regex(MOBILE_PATTERN, 'Enter a valid 10-digit mobile number'),
  details: ownDetailsSchema,
  /** Asked for only once the mobile number or the bank account has changed. */
  currentPassword: z
    .string()
    .max(128)
    .transform((value) => value || undefined),
});

export type OwnProfileFormValues = z.input<typeof ownProfileSchema>;
export type OwnProfilePayload = z.output<typeof ownProfileSchema>;

/** The bank fields — changing any of them needs the current password. */
export const BANK_FIELDS = [
  'bankName',
  'bankAccountNumber',
  'bankIfsc',
  'bankAccountHolder',
] as const;
